import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Container,
  Grid,
  TextField,
  Tabs,
  Tab,
  Button,
  Stack,
  IconButton,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  alpha,
  useTheme,
  Fab,
  Zoom,
} from "@mui/material";
import {
  GridView,
  ViewList,
  Search,
  School,
  Explore,
  KeyboardArrowUp,
  FilterList,
} from "@mui/icons-material";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import Catalog from "../Components/CoursePages/Catalog";
import Catalog2 from "../Components/CoursePages/Catalog2";
import StudentSidebar from "./StudentSdebar";

const GOLD = "#D4AF37";
const API_BASE_URL = "https://tdtlworld.com/dss-kaas-backend/";

export default function MyCourses() {
  const theme = useTheme();
  const [viewType, setViewType] = useState("grid");
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [pageTab, setPageTab] = useState(0); // 0: My Courses, 1: Explore
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  const token = localStorage.getItem("jwttoken");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [courseResp, categoryResp] = await Promise.all([
          axios.get(`${API_BASE_URL}lms/courses/`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_BASE_URL}lms/categories/`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        setCourses(courseResp.data.courses || []);
        setEnrolledCourses((courseResp.data.courses || []).filter(c => c.enrolled));
        setCategories(categoryResp.data || []);
      } catch (err) {
        console.error("Fetch failed", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const filteredCourses = (pageTab === 0 ? enrolledCourses : courses).filter(course => {
    const matchesSearch = (course.title || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || (course.categories && course.categories.includes(selectedCategory));
    return matchesSearch && matchesCategory;
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", bgcolor: "#fcfcfc" }}>
      <Header />
      
      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
        <StudentSidebar onToggle={setSidebarExpanded} />

        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            height: "calc(100vh - 64px)", 
            overflowY: "auto",
            transition: theme.transitions.create("width", { duration: 300 }),
            width: sidebarExpanded ? "calc(100% - 260px)" : "calc(100% - 80px)",
          }}
        >
          {/* Hero Section */}
          <Box 
            sx={{ 
              pt: 10, 
              pb: 8, 
              bgcolor: "#1A1A1A", 
              color: "white", 
              textAlign: "center",
              position: "relative",
              overflow: "hidden"
            }}
          >
            <Container maxWidth="lg">
              <Typography variant="h2" fontWeight={900} gutterBottom sx={{ letterSpacing: "-1px" }}>
                {pageTab === 0 ? "My Learning" : "Unlock Your Potential"}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.7, mb: 4, fontWeight: 400 }}>
                {pageTab === 0 
                  ? "Continue where you left off and master new skills."
                  : "Explore our expert-led courses and start your journey today."}
              </Typography>
              
              <Paper 
                elevation={0}
                sx={{ 
                  maxWidth: 800, 
                  mx: "auto", 
                  p: 0.5, 
                  borderRadius: "50px", 
                  bgcolor: "rgba(255,255,255,0.1)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid rgba(255,255,255,0.1)"
                }}
              >
                <Stack direction="row" spacing={1} sx={{ bgcolor: "white", borderRadius: "50px", p: 1 }}>
                  <TextField
                    fullWidth
                    placeholder="What do you want to learn today?"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    variant="standard"
                    InputProps={{
                      disableUnderline: true,
                      sx: { px: 3, fontWeight: 500, color: "black" },
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: GOLD }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button 
                    variant="contained" 
                    sx={{ 
                      borderRadius: "50px", 
                      px: 4, 
                      bgcolor: GOLD,
                      fontWeight: 800,
                      display: { xs: "none", sm: "flex" },
                      "&:hover": { bgcolor: alpha(GOLD, 0.8) }
                    }}
                  >
                    Search
                  </Button>
                </Stack>
              </Paper>
            </Container>
          </Box>

          {/* Control Bar */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", position: "sticky", top: 0, bgcolor: "white", zIndex: 10 }}>
            <Container maxWidth="lg">
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ py: 1 }}>
                <Tabs 
                  value={pageTab} 
                  onChange={(e, v) => setPageTab(v)}
                  sx={{
                    "& .MuiTabs-indicator": { bgcolor: GOLD, height: 3, borderRadius: 3 }
                  }}
                >
                  <Tab icon={<School fontSize="small" />} iconPosition="start" label="My Courses" sx={{ fontWeight: 800, fontSize: "1.0rem" }} />
                  <Tab icon={<Explore fontSize="small" />} iconPosition="start" label="Explore All" sx={{ fontWeight: 800, fontSize: "1.0rem" }} />
                </Tabs>

                <Stack direction="row" spacing={2} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel sx={{ fontWeight: 600 }}>Category</InputLabel>
                    <Select
                      value={selectedCategory}
                      label="Category"
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      sx={{ borderRadius: "50px", fontWeight: 600 }}
                    >
                      <MenuItem value="">All Categories</MenuItem>
                      {categories.map(cat => (
                        <MenuItem key={cat.id} value={cat.name}>{cat.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Stack direction="row" sx={{ bgcolor: "#eee", borderRadius: "50px", p: 0.5 }}>
                    <IconButton 
                      size="small" 
                      onClick={() => setViewType("grid")}
                      sx={{ bgcolor: viewType === "grid" ? "white" : "transparent", transition: "0.2s" }}
                    >
                      <GridView fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => setViewType("list")}
                      sx={{ bgcolor: viewType === "list" ? "white" : "transparent", transition: "0.2s" }}
                    >
                      <ViewList fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </Stack>
            </Container>
          </Box>

          {/* Grid Content Area */}
          <Container maxWidth="lg" sx={{ py: 6 }}>
            {loading ? (
              <Box sx={{ textAlign: "center", py: 10 }}>
                <CircularProgress sx={{ color: GOLD }} />
                <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>Curating courses for you...</Typography>
              </Box>
            ) : filteredCourses.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 15 }}>
                <Box 
                  component="img" 
                  src="https://cdn-icons-png.flaticon.com/512/4076/4076478.png" 
                  sx={{ width: 120, mb: 3, opacity: 0.2, filter: "grayscale(1)" }} 
                />
                <Typography variant="h5" fontWeight={800} gutterBottom>No courses found</Typography>
                <Typography variant="body2" color="text.secondary">Try adjusting your filters or search terms.</Typography>
              </Box>
            ) : (
              <Grid container spacing={viewType === "grid" ? 3 : 2}>
                {filteredCourses.map(course => (
                  <Grid item xs={12} sm={viewType === "grid" ? 6 : 12} md={viewType === "grid" ? 4 : 12} key={course.course_id}>
                    {viewType === "grid" ? (
                      <Catalog
                        coursetitle={course.title}
                        instructor={course.instructor.name}
                        courseId={course.course_id}
                        price={course.price}
                        cover_img={course.cover_img}
                        isWishlisted={course.wishlisted}
                      />
                    ) : (
                      <Catalog2
                        coursetitle={course.title}
                        description={course.description}
                        instructor={course.instructor.name}
                        courseId={course.course_id}
                        price={course.price}
                        start_date={course.start_date}
                        numlesson={course.num_lessons || 0}
                        cover_img={course.cover_img}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>
            )}
          </Container>
          <Footer />
        </Box>
      </Box>
    </Box>
  );
}
